import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { getAuthHeader } from 'helpers/auth';
import {
  WifiOff, Wifi, Database, RefreshCw, Radio, Play, ActivitySquare, Clock,
  BarChart3, Activity, Mic, MicOff, Calendar, Timer, Zap, User, MapPin,
  ChevronLeft, ChevronRight
} from 'lucide-react';

// ── Design tokens ─────────────────────────
const T = {
  bg:      'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  surf2:   'var(--nd-surface-2)',
  surf3:   'var(--nd-surface-2)',
  border:  'var(--nd-w08)',
  bord2:   'var(--nd-w10)',
  text:    'var(--nd-text)',
  muted:   'var(--nd-tx35)',
  faint:   'var(--nd-w10)',
  blue:    '#3B82F6',
  teal:    '#22D3A5',
  amber:   '#FBBF24',
  red:     '#F87171',
  purple:  '#A78BFA',
  green:   '#34D399',
  cyan:    '#22D3EE',
  pink:    '#F472B6',
  font:    "'DM Sans', sans-serif",
  mono:    "'DM Mono', monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin   { to { transform: rotate(360deg); } }
  @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes waveBar { 0%,100% { transform: scaleY(0.4); } 50% { transform: scaleY(1.0); } }

  .nt-stat-card { transition: transform 0.2s, box-shadow 0.2s; animation: fadeUp 0.35s ease both; }
  .nt-stat-card:hover { transform: translateY(-3px); box-shadow: 0 14px 32px rgba(0,0,0,0.45) !important; }

  .nt-tab {
    flex: 1; padding: 10px 20px; border: none; border-radius: 9px;
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 700;
    cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 7px;
    transition: all 0.18s;
  }
  .nt-tab-inactive { background: transparent; color: var(--nd-tx30); }
  .nt-tab-inactive:hover { background: var(--nd-w05); color: var(--nd-tx55); }
  .nt-tab-active   { background: linear-gradient(135deg,#3B82F6,#22D3A5); color: var(--nd-text-strong); box-shadow: 0 4px 14px rgba(59,130,246,0.30); }

  .nt-th {
    padding: 11px 14px; text-align: left;
    font-size: 9px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--nd-tx30); font-family: 'DM Sans', sans-serif;
    border-bottom: 1px solid var(--nd-w08); white-space: nowrap;
  }

  .nt-tr { border-bottom: 1px solid var(--nd-w05); transition: background 0.15s; }
  .nt-tr:last-child { border-bottom: none; }
  .nt-tr:hover { background: var(--nd-w03) !important; }

  .nt-td { padding: 13px 14px; vertical-align: middle; }

  .nt-refresh-btn {
    display: flex; align-items: center; gap: 7px;
    padding: 8px 16px; border: 1px solid var(--nd-w12); border-radius: 9px;
    background: var(--nd-w05); color: var(--nd-tx70);
    font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 700;
    cursor: pointer; transition: all 0.18s;
  }
  .nt-refresh-btn:hover:not(:disabled) { background: var(--nd-w10); color: var(--nd-text); border-color: var(--nd-tx22); }
  .nt-refresh-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .nt-page-btn {
    display: flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; border-radius: 8px;
    border: 1px solid var(--nd-w12); background: var(--nd-w05); color: var(--nd-tx70);
    cursor: pointer; transition: all 0.15s;
  }
  .nt-page-btn:hover:not(:disabled) { background: var(--nd-w10); color: var(--nd-text); }
  .nt-page-btn:disabled { opacity: 0.3; cursor: not-allowed; }

  .nt-audio-player {
    display: flex; align-items: center; gap: 8px;
    background: rgba(34,211,165,0.08); border: 1px solid rgba(34,211,165,0.22);
    border-radius: 10px; padding: 7px 12px; min-width: 200px; max-width: 240px;
  }
  .nt-audio-play-btn {
    width: 28px; height: 28px; border-radius: 7px; border: none; cursor: pointer;
    background: linear-gradient(135deg, #22D3A5, #3B82F6);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; transition: all 0.18s; box-shadow: 0 3px 10px rgba(34,211,165,0.30);
  }
  .nt-audio-play-btn:hover { transform: scale(1.08); box-shadow: 0 5px 16px rgba(34,211,165,0.45); }
  .nt-audio-play-btn:active { transform: scale(0.96); }

  .nt-audio-wave { display: flex; align-items: center; gap: 2px; flex: 1; }
  .nt-audio-wave span {
    display: inline-block; width: 3px; border-radius: 3px;
    background: linear-gradient(180deg, #22D3A5, #3B82F6); transform-origin: bottom;
  }
  .nt-audio-wave.playing span:nth-child(1) { animation: waveBar 0.8s ease-in-out 0.00s infinite; }
  .nt-audio-wave.playing span:nth-child(2) { animation: waveBar 0.8s ease-in-out 0.10s infinite; }
  .nt-audio-wave.playing span:nth-child(3) { animation: waveBar 0.8s ease-in-out 0.20s infinite; }
  .nt-audio-wave.playing span:nth-child(4) { animation: waveBar 0.8s ease-in-out 0.15s infinite; }
  .nt-audio-wave.playing span:nth-child(5) { animation: waveBar 0.8s ease-in-out 0.05s infinite; }

  .nt-audio-time { font-family: 'DM Mono', monospace; font-size: 10px; color: var(--nd-tx40); flex-shrink: 0; }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--nd-w10); border-radius: 4px; }
`;

const getActivityCfg = (type) => {
  const map = {
    mopping:  { color: '#60A5FA', bg: 'rgba(59,130,246,0.12)',  border: 'rgba(59,130,246,0.28)'  },
    brushing: { color: '#F472B6', bg: 'rgba(244,114,182,0.12)', border: 'rgba(244,114,182,0.28)' },
    screwing: { color: '#FBBF24', bg: 'rgba(251,191,36,0.12)',  border: 'rgba(251,191,36,0.28)'  },
    walking:  { color: '#34D399', bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.28)'  },
    banana:   { color: '#FBBF24', bg: 'rgba(251,191,36,0.12)',  border: 'rgba(251,191,36,0.28)'  },
  };
  return map[type?.toLowerCase()] || { color: 'var(--nd-tx55)', bg: 'var(--nd-w08)', border: 'var(--nd-w15)' };
};

function UserAvatar({ name, size = 40 }) {
  const gradients = [
    ['#667eea','#764ba2'],['#f093fb','#f5576c'],['#4facfe','#00f2fe'],
    ['#43e97b','#38f9d7'],['#fa709a','#fee140'],['#30cfd0','var(--nd-surface)'],
  ];
  const hash = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const [c1, c2] = gradients[hash % gradients.length];
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div style={{
      width: size, height: size, minWidth: size, borderRadius: 10,
      background: `linear-gradient(135deg, ${c1}, ${c2})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--nd-text-strong)', fontWeight: 700, fontSize: size * 0.35,
      boxShadow: '0 3px 10px rgba(0,0,0,0.30)', letterSpacing: '0.5px', flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

function AudioPlayer({ src }) {
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0); 
  const [duration, setDuration] = useState(0); 
  const [progress, setProgress] = useState(0); 
  const audioRef = useRef(null); 

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const secs = Math.floor(s);
    return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
  };

  const getAudio = useCallback(() => {
    if (audioRef.current) return audioRef.current;
    const audio = new Audio(src);
    audio.preload = 'none';
    audio.addEventListener('loadedmetadata', () => { if (audio.duration && isFinite(audio.duration)) setDuration(audio.duration); });
    audio.addEventListener('durationchange', () => { if (audio.duration && isFinite(audio.duration)) setDuration(audio.duration); });
    audio.addEventListener('timeupdate', () => {
      setCurrent(audio.currentTime);
      if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
        setProgress((audio.currentTime / audio.duration) * 100);
        setDuration(d => d || audio.duration);
      }
    });
    audio.addEventListener('ended', () => { setPlaying(false); setCurrent(0); setProgress(0); });
    audioRef.current = audio;
    return audio;
  }, [src]);

  useEffect(() => {
    return () => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; audioRef.current = null; }
    };
  }, []);

  const toggle = () => {
    const audio = getAudio();
    if (playing) { audio.pause(); setPlaying(false); }
    else { audio.play().then(() => setPlaying(true)).catch((err) => console.warn('Audio play failed:', err)); }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    audio.currentTime = ratio * audio.duration;
  };

  return (
    <div className="nt-audio-player" style={{ flexDirection: 'column', gap: 6, minWidth: 210 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
        <button className="nt-audio-play-btn" onClick={toggle}>
          {playing
            ? <svg width="10" height="12" viewBox="0 0 10 12" fill="white"><rect x="0" y="0" width="3.5" height="12" rx="1.5"/><rect x="6.5" y="0" width="3.5" height="12" rx="1.5"/></svg>
            : <svg width="10" height="12" viewBox="0 0 10 12" fill="white" style={{ marginLeft: 1 }}><path d="M0 0 L10 6 L0 12 Z"/></svg>
          }
        </button>
        <div className={`nt-audio-wave ${playing ? 'playing' : ''}`} style={{ flex: 1 }}>
          {[18, 26, 20, 30, 16].map((h, i) => <span key={i} style={{ height: h }} />)}
        </div>
        <span className="nt-audio-time">{fmt(current)}<span style={{ opacity: 0.4 }}>/</span>{fmt(duration)}</span>
      </div>
      <div onClick={handleSeek} style={{ width: '100%', height: 3, borderRadius: 99, background: 'var(--nd-w10)', cursor: 'pointer', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg, #22D3A5, #3B82F6)', borderRadius: 99, transition: 'width 0.1s linear' }} />
      </div>
    </div>
  );
}

function buildUserSummary(data) {
  if (!Array.isArray(data)) return [];
  const map = {};
  data.forEach(item => {
    if (!item) return; 
    const key = item.user_id || item.device_id || 'unknown';
    if (!map[key]) {
      map[key] = {
        key, name: item.user_name || item.device_id || `Device #${key}`,
        activities: {}, totalRecords: 0, voiceNotes: 0,
      };
    }
    const u = map[key];
    u.totalRecords++;
    if (item.voice_note_path) u.voiceNotes++;
    
    const act = item.activity_name || 'unknown';
    u.activities[act] = (u.activities[act] || 0) + 1;
    
    if (item.recorded_at) {
      const time = new Date(item.recorded_at).getTime();
      if (!isNaN(time)) {
        if (!u.firstTime || time < u.firstTime) { u.firstTime = time; u.firstSeen = item.recorded_at; }
        if (!u.lastTime || time > u.lastTime)   { u.lastTime = time; u.lastSeen = item.recorded_at; }
      }
    }
  });
  
  return Object.values(map).map(u => {
    const topActivity = Object.entries(u.activities).sort((a, b) => b[1] - a[1])[0];
    const durationMs = (u.lastTime && u.firstTime) ? u.lastTime - u.firstTime : 0;
    const durationMin = Math.round(durationMs / 60000);
    return { ...u, topActivity: topActivity ? topActivity[0] : '—', durationMin };
  });
}

// One line per activity PER DAY — every raw wrist row collapsed into its
// (activity, calendar day) bucket, with a running total of data points.
// Grouping by activity name alone would merge "Brushing" done today with
// "Brushing" done back in April into a single row spanning that whole range —
// grouping by day too keeps each day's activity as its own line.
function buildActivitySummary(data) {
  if (!Array.isArray(data)) return [];
  const map = {};
  data.forEach(item => {
    if (!item) return;
    const act = item.activity_name || 'Idle';
    const time = item.recorded_at ? new Date(item.recorded_at).getTime() : NaN;
    const dayKey = !isNaN(time) ? new Date(time).toDateString() : 'Unknown Date';
    const key = `${act}__${dayKey}`;
    if (!map[key]) {
      map[key] = { activity: act, day: dayKey, dataPoints: 0, voiceNotes: 0, firstTime: 0, lastTime: 0, firstSeen: null, lastSeen: null, sampleVoicePath: null, sampleVoiceTime: 0 };
    }
    const a = map[key];
    a.dataPoints++;
    if (item.voice_note_path) {
      a.voiceNotes++;
      const vTime = !isNaN(time) ? time : 0;
      if (vTime >= a.sampleVoiceTime) { a.sampleVoiceTime = vTime; a.sampleVoicePath = item.voice_note_path; }
    }
    if (!isNaN(time)) {
      if (!a.firstTime || time < a.firstTime) { a.firstTime = time; a.firstSeen = item.recorded_at; }
      if (!a.lastTime || time > a.lastTime)   { a.lastTime = time; a.lastSeen = item.recorded_at; }
    }
  });

  return Object.values(map).sort((a, b) => b.lastTime - a.lastTime);
}

// ══════════════════════════════════════════════════════════════════
//   MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════
const NovaaWristTracking = () => {
  const [trackingData, setTrackingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState('log');

  // Job-linked tracker data (real FO name / client / site — tracker_data table,
  // via /api/tracker/*), separate from the wrist-sensor stream above.
  const [trackerDashboard, setTrackerDashboard] = useState({
    totalDevices: 0, onlineDevices: 0, activeJobs: 0, totalDataPoints: 0,
  });
  const [trackerActivityLog, setTrackerActivityLog] = useState([]);
  const [trackerFOSummary, setTrackerFOSummary] = useState([]);

  // PAGINATION STATES
  const [currentLogPage, setCurrentLogPage] = useState(1);
  const rowsPerPage = 100; // Ek page pe kitne records dikhane hain

  useEffect(() => { fetchData(); }, []);

  const fetchData = async (isSilentRefresh = false) => {
    try {
      if (!isSilentRefresh) setLoading(true);
      setRefreshing(true);

      const config = getAuthHeader();
      const base = process.env.REACT_APP_API_BASE_URL;

      // Removed limits to fetch ALL data
      const [wristRes, trackerDashRes, trackerLogRes, trackerSummaryRes] = await Promise.all([
        axios.get(`${base}/wristtracker/data`, config),
        axios.get(`${base}/tracker/dashboard`, config).catch(() => null),
        axios.get(`${base}/tracker/activity-log`, config).catch(() => null),
        axios.get(`${base}/tracker/activity-summary`, config).catch(() => null),
      ]);

      let safeData = [];
      if (Array.isArray(wristRes?.data)) safeData = wristRes.data;
      else if (Array.isArray(wristRes?.data?.data)) safeData = wristRes.data.data;
      else if (Array.isArray(wristRes?.data?.rows)) safeData = wristRes.data.rows;

      setTrackingData(safeData);
      setTrackerDashboard({
        totalDevices: trackerDashRes?.data?.totalDevices || 0,
        onlineDevices: trackerDashRes?.data?.onlineDevices || 0,
        activeJobs: trackerDashRes?.data?.activeJobs || 0,
        totalDataPoints: trackerDashRes?.data?.totalDataPoints || 0,
      });
      setTrackerActivityLog(Array.isArray(trackerLogRes?.data) ? trackerLogRes.data : []);
      setTrackerFOSummary(Array.isArray(trackerSummaryRes?.data) ? trackerSummaryRes.data : []);

      setCurrentLogPage(1);
    } catch (e) {
      console.error('Fetch Error:', e);
      setTrackingData([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => fetchData(true);

  const formatTime = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }); } 
    catch(e) { return '—'; }
  };
  
  const formatDate = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } 
    catch(e) { return '—'; }
  };
  
  const formatDuration = (minutes) => {
    if (!minutes || minutes <= 0) return '0m';
    const h = Math.floor(minutes / 60), m = minutes % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };
  
  const getAudioUrl = (dbPath) => {
    if (!dbPath) return '';
    const base = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/api$/, '');
    return `${base}${dbPath}`;
  };

  // Perform expensive operations only when complete data changes
  const totalVoiceNotes = useMemo(() => trackingData.filter(d => d?.voice_note_path).length, [trackingData]);
  const uniqueActivities = useMemo(() => new Set(trackingData.map(d => d?.activity_name).filter(Boolean)).size, [trackingData]);
  const userSummary = useMemo(() => buildUserSummary(trackingData), [trackingData]);

  const activitySummary = useMemo(() => buildActivitySummary(trackingData), [trackingData]);

  const totalLogPages = Math.ceil(trackerActivityLog.length / rowsPerPage) || 1;
  const displayLog = useMemo(() => {
    const startIndex = (currentLogPage - 1) * rowsPerPage;
    return trackerActivityLog.slice(startIndex, startIndex + rowsPerPage);
  }, [trackerActivityLog, currentLogPage]);

  if (loading) return (
    <div style={{ background: T.bg, fontFamily: T.font, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <style>{STYLES}</style>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 44, height: 44, border: `3px solid ${T.border}`, borderTop: `3px solid ${T.blue}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 14px' }} />
        <div style={{ color: T.muted, fontSize: 13 }}>Downloading Complete Database…<br/><span style={{fontSize: 10, opacity: 0.6}}>(Large datasets may take a moment)</span></div>
      </div>
    </div>
  );

  const onlinePct = trackerDashboard.totalDevices > 0
    ? Math.round((trackerDashboard.onlineDevices / trackerDashboard.totalDevices) * 100)
    : 0;

  // Combined total across both tables — every data point that has ever landed
  // in the database, regardless of which device/table it came in through.
  const combinedTotalDataPoints = trackingData.length + trackerDashboard.totalDataPoints;

  const statCards = [
    { label: 'Total Data Points',    value: combinedTotalDataPoints.toLocaleString(),            sub: 'Wrist Sensor + NOVAA Tracker', color: T.blue, Icon: Database,       delay: 0    },
    { label: 'Online Now',           value: trackerDashboard.onlineDevices,                      sub: `${onlinePct}% of ${trackerDashboard.totalDevices} devices`, color: T.green, Icon: Wifi, delay: 0.04 },
    { label: 'Active Jobs',          value: trackerDashboard.activeJobs,                         sub: 'Today',                color: T.amber,  Icon: Activity,       delay: 0.08 },
    { label: 'Voice Notes',          value: totalVoiceNotes.toLocaleString(),                    sub: 'Audio Captures',       color: T.teal,   Icon: Mic,            delay: 0.12 },
    { label: 'Activity Types',       value: uniqueActivities,                                    sub: 'Detected Gestures',    color: T.purple, Icon: ActivitySquare, delay: 0.16 },
  ];

  return (
    <div style={{ background: T.bg, fontFamily: T.font, color: T.text, padding: '14px 20px', minHeight: '100vh' }}>
      <style>{STYLES}</style>

      {/* ── HEADER ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, marginBottom: 18, overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
          <div>
            <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 5 }}>NOVAA · DATA COLLECTION</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.03em', lineHeight: 1 }}>Overall Data Collection</div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 5 }}>Devices, Field Activity &amp; Sensor Telemetry — All Sources</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            <button className="nt-refresh-btn" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw size={14} style={{ animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }} />
              {refreshing ? 'Syncing Complete Data…' : 'Sync Complete Data'}
            </button>
          </div>
        </div>
        <div style={{ height: 2, background: `linear-gradient(90deg, ${T.blue}, ${T.teal}, transparent)` }} />
      </div>

      {/* ── STATS ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 14, marginBottom: 18 }}>
        {statCards.map((s) => (
          <div key={s.label} className="nt-stat-card" style={{
            background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
            padding: '18px 20px', boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            animationDelay: `${s.delay}s`, position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: 0, right: 0, width: 70, height: 70, borderRadius: '0 14px 0 70px', background: `${s.color}0C` }} />
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: `linear-gradient(180deg,${s.color},transparent)`, borderRadius: '14px 0 0 14px' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase' }}>{s.label}</div>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: `${s.color}18`, border: `1px solid ${s.color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <s.Icon size={14} color={s.color} />
              </div>
            </div>
            <div style={{ fontSize: 34, fontWeight: 900, color: s.color, fontFamily: T.mono, letterSpacing: '-1px', lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 5 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ── TOGGLE ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 6, marginBottom: 18, display: 'flex', gap: 6 }}>
        <button className={`nt-tab ${viewMode === 'log' ? 'nt-tab-active' : 'nt-tab-inactive'}`} onClick={() => setViewMode('log')}>
          <ActivitySquare size={15} /> Raw Telemetry
        </button>
        <button className={`nt-tab ${viewMode === 'summary' ? 'nt-tab-active' : 'nt-tab-inactive'}`} onClick={() => setViewMode('summary')}>
          <BarChart3 size={15} /> User Summary
        </button>
        <button className={`nt-tab ${viewMode === 'activity' ? 'nt-tab-active' : 'nt-tab-inactive'}`} onClick={() => setViewMode('activity')}>
          <Activity size={15} /> Activity Log
        </button>
        <button className={`nt-tab ${viewMode === 'fo-summary' ? 'nt-tab-active' : 'nt-tab-inactive'}`} onClick={() => setViewMode('fo-summary')}>
          <Radio size={15} /> FO Summary
        </button>
      </div>

      {/* ── RAW DATA ── */}
      {viewMode === 'log' && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ActivitySquare size={14} color={T.blue} />
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Raw Telemetry — By Activity</span>
            </div>
          </div>

          {activitySummary.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 52, marginBottom: 14 }}>📡</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 6 }}>No telemetry data found</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th className="nt-th" style={{ paddingLeft: 20 }}>Activity Trigger</th>
                    <th className="nt-th">Date</th>
                    <th className="nt-th">Time Range</th>
                    <th className="nt-th" style={{ textAlign: 'center' }}>Voice Notes</th>
                    <th className="nt-th">Data Points</th>
                  </tr>
                </thead>
                <tbody>
                  {activitySummary.map((a) => {
                    const ac = getActivityCfg(a.activity);
                    return (
                      <tr key={`${a.activity}__${a.day}`} className="nt-tr">
                        <td className="nt-td" style={{ paddingLeft: 20 }}>
                          <span style={{
                            background: ac.bg, color: ac.color, border: `1px solid ${ac.border}`,
                            padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                            textTransform: 'capitalize', letterSpacing: '0.06em', fontFamily: T.mono,
                          }}>
                            {a.activity}
                          </span>
                        </td>
                        <td className="nt-td">
                          <div style={{ fontSize: 12, color: T.text, fontWeight: 600, fontFamily: T.mono, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Calendar size={10} /> {formatDate(a.firstSeen)}
                          </div>
                        </td>
                        <td className="nt-td">
                          <div style={{ fontFamily: T.mono, fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Clock size={10} color={T.muted} />
                            <span style={{ color: T.green }}>{formatTime(a.firstSeen)}</span>
                            <span style={{ color: T.faint }}>→</span>
                            <span style={{ color: T.red }}>{formatTime(a.lastSeen)}</span>
                          </div>
                        </td>
                        <td className="nt-td">
                          {a.sampleVoicePath ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
                              <AudioPlayer src={getAudioUrl(a.sampleVoicePath)} />
                              <span style={{ fontSize: 10, color: T.muted, fontFamily: T.mono }}>
                                Latest of {a.voiceNotes} note{a.voiceNotes === 1 ? '' : 's'}
                              </span>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: T.faint, fontSize: 12, fontStyle: 'italic' }}>
                              <MicOff size={14} /> No Audio
                            </div>
                          )}
                        </td>
                        <td className="nt-td">
                          <div style={{ fontSize: 20, fontWeight: 800, color: T.blue, fontFamily: T.mono }}>{a.dataPoints.toLocaleString()}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── SUMMARY DATA ── */}
      {viewMode === 'summary' && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 9 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(34,211,165,0.12)', border: '1px solid rgba(34,211,165,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BarChart3 size={14} color={T.teal} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>User Activity Summary</span>
          </div>

          {userSummary.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 52, marginBottom: 14 }}>⌚</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 6 }}>No user data available</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th className="nt-th" style={{ paddingLeft: 20 }}>User / Device</th>
                    <th className="nt-th" style={{ textAlign: 'center' }}>Session Duration</th>
                    <th className="nt-th" style={{ textAlign: 'center' }}>Total Records</th>
                    <th className="nt-th" style={{ textAlign: 'center' }}>Top Activity</th>
                    <th className="nt-th" style={{ textAlign: 'center' }}>Activity Types</th>
                    <th className="nt-th" style={{ textAlign: 'center' }}>Voice Notes</th>
                    <th className="nt-th">First → Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {userSummary.map((u) => {
                    const ac = getActivityCfg(u?.topActivity);
                    return (
                      <tr key={u?.key} className="nt-tr">
                        <td className="nt-td" style={{ paddingLeft: 20 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                            <UserAvatar name={u?.name} size={40} />
                            <div>
                              <div style={{ fontWeight: 700, color: T.text, fontSize: 13, marginBottom: 3 }}>{u?.name}</div>
                              <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>ID: {u?.key}</div>
                            </div>
                          </div>
                        </td>
                        <td className="nt-td" style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                            <div style={{ width: 24, height: 24, borderRadius: 6, background: 'rgba(251,191,36,0.10)', border: '1px solid rgba(251,191,36,0.20)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Timer size={11} color={T.amber} />
                            </div>
                            <span style={{ fontSize: 18, fontWeight: 800, color: T.amber, fontFamily: T.mono }}>{formatDuration(u?.durationMin)}</span>
                          </div>
                        </td>
                        <td className="nt-td" style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 800, color: T.blue, fontFamily: T.mono }}>{u?.totalRecords?.toLocaleString()}</div>
                        </td>
                        <td className="nt-td" style={{ textAlign: 'center' }}>
                          <span style={{
                            background: ac.bg, color: ac.color, border: `1px solid ${ac.border}`,
                            padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                            textTransform: 'capitalize', letterSpacing: '0.06em', fontFamily: T.mono,
                          }}>{u?.topActivity}</span>
                        </td>
                        <td className="nt-td" style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 800, color: T.cyan, fontFamily: T.mono }}>{Object.keys(u?.activities || {}).length}</div>
                        </td>
                        <td className="nt-td" style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                            <div style={{ width: 22, height: 22, borderRadius: 6, background: u?.voiceNotes > 0 ? 'rgba(34,211,165,0.12)' : 'var(--nd-w05)', border: `1px solid ${u?.voiceNotes > 0 ? 'rgba(34,211,165,0.28)' : 'var(--nd-w10)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {u?.voiceNotes > 0 ? <Mic size={11} color={T.teal} /> : <MicOff size={11} color={T.faint} />}
                            </div>
                            <span style={{ fontSize: 18, fontWeight: 800, color: u?.voiceNotes > 0 ? T.teal : T.muted, fontFamily: T.mono }}>{u?.voiceNotes}</span>
                          </div>
                        </td>
                        <td className="nt-td">
                          <div style={{ fontFamily: T.mono, fontSize: 11 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
                              <span style={{ fontSize: 9, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', width: 32 }}>From</span>
                              <span style={{ color: T.green }}>{formatTime(u?.firstSeen)}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <span style={{ fontSize: 9, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', width: 32 }}>To</span>
                              <span style={{ color: T.red }}>{formatTime(u?.lastSeen)}</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── ACTIVITY LOG (job-linked NOVAA Tracker data) ── */}
      {viewMode === 'activity' && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 9 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Activity size={14} color={T.blue} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Field Activity Log</span>
          </div>

          {trackerActivityLog.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 52, marginBottom: 14 }}>📊</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 6 }}>No activity found</div>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th className="nt-th" style={{ paddingLeft: 20 }}>Field Operator</th>
                      <th className="nt-th">Activity Type</th>
                      <th className="nt-th">Client &amp; Site</th>
                      <th className="nt-th">Time Period</th>
                      <th className="nt-th">Duration</th>
                      <th className="nt-th">Data Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayLog.map((activity, index) => {
                      const ac = getActivityCfg(activity.activity_type);
                      const name = `${activity.first_name || ''} ${activity.last_name || ''}`.trim() || 'Unknown';
                      return (
                        <tr key={`${activity.user_id}-${activity.schedule_id}-${activity.activity_type}-${index}`} className="nt-tr">
                          <td className="nt-td" style={{ paddingLeft: 20 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <UserAvatar name={name} size={32} />
                              <div>
                                <div style={{ fontWeight: 600, color: T.text, fontSize: 13 }}>{name}</div>
                                <div style={{ fontSize: 11, color: T.muted }}>{activity.department || 'Field Operator'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="nt-td">
                            <span style={{
                              background: ac.bg, color: ac.color, border: `1px solid ${ac.border}`,
                              padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                              textTransform: 'capitalize', letterSpacing: '0.06em', fontFamily: T.mono,
                            }}>
                              {activity.activity_type || 'Unknown'}
                            </span>
                          </td>
                          <td className="nt-td">
                            {activity.client_name ? (
                              <div>
                                <div style={{ fontSize: 13, color: T.text, fontWeight: 600, marginBottom: 3 }}>{activity.client_name}</div>
                                <div style={{ fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <MapPin size={10} /> {activity.site_name}
                                </div>
                              </div>
                            ) : (
                              <div style={{ fontSize: 12, color: T.muted, fontStyle: 'italic' }}>No job assigned</div>
                            )}
                          </td>
                          <td className="nt-td">
                            <div style={{ fontSize: 12, color: T.text, fontWeight: 600, fontFamily: T.mono, marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Clock size={10} />
                              {activity.activity_end
                                ? `${formatTime(activity.activity_start)} – ${formatTime(activity.activity_end)}`
                                : formatTime(activity.activity_start)}
                            </div>
                            <div style={{ fontSize: 10, color: T.faint, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Calendar size={10} /> {formatDate(activity.scheduled_date || activity.activity_start)}
                            </div>
                          </td>
                          <td className="nt-td">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Timer size={11} color={T.amber} />
                              <span style={{ fontSize: 13, color: T.text, fontWeight: 700, fontFamily: T.mono }}>
                                {formatDuration(activity.duration_minutes)}
                              </span>
                            </div>
                          </td>
                          <td className="nt-td">
                            <div style={{ fontSize: 14, color: T.teal, fontWeight: 800, fontFamily: T.mono }}>
                              {parseInt(activity.data_points, 10).toLocaleString()}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ padding: '15px 20px', borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 12, color: T.muted }}>
                  Showing <strong style={{ color: T.text }}>{(currentLogPage - 1) * rowsPerPage + 1}</strong> to <strong style={{ color: T.text }}>{Math.min(currentLogPage * rowsPerPage, trackerActivityLog.length)}</strong> of <strong style={{ color: T.text }}>{trackerActivityLog.length}</strong> records
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button className="nt-page-btn" disabled={currentLogPage === 1} onClick={() => setCurrentLogPage(p => p - 1)}>
                    <ChevronLeft size={16} />
                  </button>
                  <span style={{ fontSize: 12, fontWeight: 700, fontFamily: T.mono, margin: '0 8px' }}>
                    Page {currentLogPage} / {totalLogPages}
                  </span>
                  <button className="nt-page-btn" disabled={currentLogPage === totalLogPages} onClick={() => setCurrentLogPage(p => p + 1)}>
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── FO PERFORMANCE SUMMARY (job-linked NOVAA Tracker data) ── */}
      {viewMode === 'fo-summary' && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 9 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Radio size={14} color={T.purple} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Field Operator Performance Summary</span>
            <span style={{ fontSize: 10, color: T.muted, fontFamily: T.mono, marginLeft: 2 }}>Last 30 Days</span>
          </div>

          {trackerFOSummary.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 52, marginBottom: 14 }}>👷</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 6 }}>No FO data available</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th className="nt-th" style={{ paddingLeft: 20 }}>Field Operator</th>
                    <th className="nt-th" style={{ textAlign: 'center' }}>Jobs Completed</th>
                    <th className="nt-th" style={{ textAlign: 'center' }}>Days Active</th>
                    <th className="nt-th" style={{ textAlign: 'center' }}>Total Hours</th>
                    <th className="nt-th" style={{ textAlign: 'center' }}>Activity Types</th>
                    <th className="nt-th">Total Data Points</th>
                  </tr>
                </thead>
                <tbody>
                  {trackerFOSummary.map((fo) => {
                    const name = `${fo.first_name || ''} ${fo.last_name || ''}`.trim() || 'Unknown';
                    return (
                      <tr key={fo.user_id} className="nt-tr">
                        <td className="nt-td" style={{ paddingLeft: 20 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                            <UserAvatar name={name} size={40} />
                            <div>
                              <div style={{ fontWeight: 700, color: T.text, fontSize: 13, marginBottom: 3 }}>{name}</div>
                              <div style={{ fontSize: 11, color: T.muted }}>
                                Last active: <span style={{ fontFamily: T.mono }}>{formatTime(fo.last_activity)}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="nt-td" style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 800, color: T.blue, fontFamily: T.mono }}>{fo.total_jobs}</div>
                        </td>
                        <td className="nt-td" style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 800, color: T.green, fontFamily: T.mono }}>{fo.days_active}</div>
                        </td>
                        <td className="nt-td" style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 800, color: T.amber, fontFamily: T.mono }}>{Math.round(fo.total_hours)}h</div>
                        </td>
                        <td className="nt-td" style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 800, color: T.cyan, fontFamily: T.mono }}>{fo.activity_types}</div>
                        </td>
                        <td className="nt-td">
                          <div style={{ fontSize: 18, fontWeight: 800, color: T.teal, fontFamily: T.mono }}>
                            {parseInt(fo.total_data_points, 10).toLocaleString()}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NovaaWristTracking;